import {
  auditFacts,
  EntityHeader,
  InlineEdit,
} from "@/shared/components/EntityPage";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type CollectionRecord } from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { EntityIcon } from "@/shared/foundation/icons";

interface CollectionHeaderProps {
  collection: CollectionRecord;
}

/**
 * The header of a collection page: name, then who created it and when, how
 * many items it holds (by their record type's icon) and its ID, then the
 * description and tags. Editing is offered to everyone who can see the
 * collection; the API refuses a change the viewer may not make.
 *
 * The count is `resource_count`, which the server keeps current as members
 * change, rather than the length of `resources`, which depends on the content
 * mode the record was fetched in.
 */
export function CollectionHeader({ collection }: CollectionHeaderProps) {
  const notify = useNotification();
  const { mutateAsync: updateCollection } = useUpdateCollection();

  // The name and description report a failed save in place; a tag, here.
  const save = (updates: Parameters<typeof updateCollection>[0]["updates"]) =>
    updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates,
    });
  const update = (updates: Parameters<typeof save>[0]) =>
    void save(updates).catch(() => notify("Couldn't save the change", "error"));

  const count = collection.resource_count;
  return (
    <EntityHeader
      type="collection"
      name={
        <InlineEdit
          value={collection.name ?? ""}
          onSave={(name) => save({ name })}
          required
          placeholder="Collection name"
          textStyle="title"
          ariaLabel="collection name"
        />
      }
      description={{
        value: collection.description,
        canEdit: true,
        onSave: (description) => save({ description }),
      }}
      tags={{
        value: collection.tags,
        canAdd: true,
        onAdd: (tag) => update({ add_tags: [tag] }),
      }}
      id={collection.collection_id}
      meta={[
        ...auditFacts({
          created: collection.created,
          createdBy: collection.created_by,
        }),
        {
          key: "items",
          icon: <EntityIcon type={collection.resource_type} />,
          value: `${count} ${collection.resource_type}${count === 1 ? "" : "s"}`,
        },
      ]}
    />
  );
}
